import { Container, Section, Heading, Lede, Eyebrow, LinkButton } from "../../components/ui";
import { Seo } from "../../lib/head";
import { CtaBand, faqPageJsonLd, FaqSection, type Faq } from "../../components/marketing";

const FIELDS: { label: string; hint: string }[] = [
  { label: "Resident identifier", hint: "Use a synthetic \"ZZ DEMO\" identifier in any shared copy — never real PHI." },
  { label: "Target medication", hint: "Drug, strength, and route under consideration for reduction." },
  { label: "Indication & diagnosis", hint: "The documented target symptom and diagnosis the drug is treating." },
  { label: "Current dose & duration", hint: "Present dose, start date, and time at the current dose." },
  { label: "GDR history", hint: "Prior reduction attempts — dates, dose changes, and outcomes (tolerated / symptoms recurred)." },
  { label: "Clinical rationale for reduction", hint: "Why reduction is appropriate now — OR a documented clinical contraindication if GDR is deemed inappropriate." },
  { label: "Target taper schedule", hint: "Proposed step-down (e.g., reduce X mg every N weeks) and the reassessment point." },
  { label: "Monitoring plan", hint: "Target symptoms, non-pharmacologic supports, and the follow-up interval." },
  { label: "Prescriber recommendation & e-signature", hint: "Accept / modify / decline with rationale, signed and dated by the prescriber." },
  { label: "DON acknowledgment", hint: "Director of Nursing acknowledgment and date, closing the loop." },
];

const FAQS: Faq[] = [
  {
    q: "When is a gradual dose reduction (GDR) required?",
    a: "Under CMS guidance (F758), residents on psychotropics should have GDR attempted in two separate quarters within the first year of admission or initiation (unless clinically contraindicated), and at least annually thereafter. The key is documenting the attempt — or a clear clinical contraindication.",
  },
  {
    q: "What if GDR is clinically contraindicated?",
    a: "Document it. A defensible record shows the clinical rationale for why reduction would likely cause harm (e.g., prior attempts led to symptom recurrence), tied to the diagnosis and target symptoms — not a blanket statement.",
  },
  {
    q: "Do I have to use real patient data?",
    a: "No. Use synthetic \"ZZ DEMO\" identifiers in any shared or sample copy. This template is guidance and implies no CMS or ASCP endorsement.",
  },
];

export function GdrTemplate() {
  return (
    <>
      <Seo
        title="Gradual Dose Reduction (GDR) Documentation Template"
        description="A free GDR documentation template for consultant pharmacists — capture the attempt, rationale, taper, and prescriber/DON sign-off aligned to CMS F758 and ASCP/Beers/STOPP guidance."
        path="/resources/gdr-documentation-template"
        jsonLd={faqPageJsonLd(FAQS)}
      />

      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Resource · GDR</Eyebrow>
            <Heading level={1} className="mt-3">
              Gradual dose reduction documentation template
            </Heading>
            <Lede className="mt-6">
              A one-page structure for a defensible GDR record — the attempt (or documented contraindication),
              the rationale, the taper, and the prescriber and DON sign-off that closes the loop.
            </Lede>
            <p className="mt-4 text-sm text-ink-500">
              Aligned to CMS F758 (psychotropics / GDR) and ASCP / Beers / STOPP criteria. Guidance only — no CMS
              or ASCP endorsement; use synthetic data, never PHI.
            </p>
          </div>
        </Container>
      </Section>

      <Section tone="soft" className="pt-0">
        <Container>
          <ol className="mx-auto max-w-3xl space-y-3">
            {FIELDS.map((f, i) => (
              <li key={f.label} className="flex gap-4 rounded-2xl border border-border bg-surface p-5">
                <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-forest-soft font-mono text-sm font-bold text-forest-700">
                  {i + 1}
                </span>
                <div>
                  <p className="font-semibold text-ink-900">{f.label}</p>
                  <p className="mt-1 text-[0.9375rem] leading-relaxed text-ink-600">{f.hint}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mx-auto mt-8 flex max-w-3xl flex-wrap gap-3">
            <LinkButton to="/survey-readiness">How the platform captures this</LinkButton>
            <LinkButton to="/resources/f-tag-readiness-checklist" variant="secondary">
              F-tag readiness checklist
            </LinkButton>
          </div>
        </Container>
      </Section>

      <FaqSection items={FAQS} />

      <CtaBand
        title="GDR documentation, captured as you review"
        body="WeConsult Rx flags overdue GDR attempts and carries the rationale into a dated, e-signed record — see it on a live monthly review."
        primary={{ to: "/demo", label: "Book a demo" }}
        secondary={{ to: "/survey-readiness", label: "Survey readiness" }}
      />
    </>
  );
}
